import { ActionFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { SAVE_GENERIC_ORDER } from '../../../../gql/mutations/generic-order';
import { SAVE_CAMPAIGN } from '../../../../gql/mutations/campaign';
import { writeOfflineOptimisticOrderEntityUpdate } from '../../../../gql/cacheHelpers';

// Types
import { OrderType } from '../../../../types/orders';
import { showSuccessToast } from '../../../../utils/toasts';
import i18n from '../../../../i18n';

/**
 * Bloque C-9: edición OT desde WorkOrderDetailed.
 *
 * Antes (4 mutations Promise.all → riesgo P2034):
 *   UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS  // sobre Order
 *   + UPDATE_GENERIC_ORDER + UPDATE_GENERIC_ORDER_RELATIONSHIPS  (rama Generic)
 *   + UPDATE_CAMPAIGN + UPDATE_CAMPAIGN_RELATIONSHIPS            (rama Campaign)
 *
 * Ahora: 1 sola SAVE_GENERIC_ORDER o SAVE_CAMPAIGN con `id` (update path).
 * Backend hace `tx.order.update` + `tx.entity.update` secuencial dentro de
 * un único `$transaction` Prisma — sin race ni P2034.
 *
 * El `input.id` de SAVE_* en update path es el id de la entidad
 * (GenericOrder.id o Campaign.id), que el form pasa como `modelTypeId`.
 * El `formData.id` (Order.id) se ignora aquí — el service localiza la
 * Order paralela desde la entidad vía `entity.orderIDs[0]`.
 */
/**
 * Detección de mutación encolada por OfflineQueueLink (mismo helper que
 * NewOrder/EditAccident/etc.). No es un error real: se replay-ea al reconectar.
 */
function isOfflineQueuedMutationError(err: unknown): boolean {
  const e = err as {
    offlineQueued?: boolean;
    message?: string;
    networkError?: { offlineQueued?: boolean };
  };
  return (
    e?.offlineQueued === true ||
    e?.networkError?.offlineQueued === true ||
    (e?.message ?? '').includes('Mutation queued for offline sync')
  );
}

export const ActionNewWorkOrderDetailed: ActionFunction = async ({
  request,
}) => {
  const formData = await request.formData();
  const pkInit = formData.get('pkInit');
  const pkEnd = formData.get('pkEnd');
  const description = formData.get('description');
  const notes = formData.get('notes');
  const date = formData.get('date');
  const capitol = formData.get('capitol');
  const subCapitol = formData.get('subCapitol');
  const concession = formData.get('concession');
  const road = formData.get('road');
  const type = formData.get('type');
  const modelTypeId = formData.get('modelTypeId');
  const classification = formData.get('classification');
  const margin = formData.get('margin');
  const direction = formData.get('direction');
  const lane = formData.get('lane');
  const track = formData.get('track');
  const predefine = formData.get('predefine');
  const orderId = formData.get('id');
  const teamRelationships = formData.get('teamRelationships');
  const teamIds: string[] = teamRelationships
    ? String(teamRelationships).split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const pkInitSplit = pkInit?.toString().replace(/ /g, '').split('+');
  const pkEndSplit = pkEnd?.toString().replace(/ /g, '').split('+');

  const sharedInput = {
    // Idempotencia online: UUID estable por operación (RetryLink no duplica).
    clientOpId: crypto.randomUUID(),
    id: modelTypeId as string,
    ...(date && { registerInitDate: date as string }),
    ...(description && { description: description as string }),
    ...(notes && { notes: notes as string }),
    ...(pkInit && {
      pkInitKm: Number(pkInitSplit?.[0]),
      pkInitMeter: Number(pkInitSplit?.[1]),
    }),
    ...(pkEnd && {
      pkEndKm: Number(pkEndSplit?.[0]),
      pkEndMeter: Number(pkEndSplit?.[1]),
    }),
    ...(track && { track: track as string }),
    ...(margin && { margin: margin as string }),
    ...(direction && { direction: direction as string }),
    ...(lane && { lane: lane as string }),
    ...(concession && { concessionId: concession as string }),
    ...(road && { roadId: road as string }),
    ...(teamIds.length > 0 && { teamIds }),
    ...(capitol && { capitolId: capitol as string }),
    ...(subCapitol && { subCapitolId: subCapitol as string }),
    ...(classification && { classification: classification as string }),
    ...(predefine && { predefinedOrderId: predefine as string }),
  };

  try {
    if (type === OrderType.Campaign) {
      await client.mutate({
        mutation: SAVE_CAMPAIGN,
        variables: { input: sharedInput },
      });
    } else {
      await client.mutate({
        mutation: SAVE_GENERIC_ORDER,
        variables: { input: sharedInput },
      });
    }
    return null;
  } catch (err) {
    // Encolada offline → no es un fallo: se replay-ea al reconectar.
    if (isOfflineQueuedMutationError(err)) {
      showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
      // Escritura optimista al encolar offline — el replay al reconectar
      // reconfirma con la respuesta real del servidor (cazado en pruebas
      // manuales I3 2026-07-04).
      writeOfflineOptimisticOrderEntityUpdate(
        client.cache,
        sharedInput,
        type === OrderType.Campaign,
        orderId ? (orderId as string) : undefined,
      );
      return null;
    }
    Sentry.captureException(err);
    console.log('> Work order updated - error updating:', err);
    // Re-lanzar → errorElement. Antes: `return null` silencioso (el guardado
    // fallaba sin ninguna señal al usuario — fallo total silencioso).
    throw err;
  }
  return null;
};
